import { ContainerInstance } from '../container-instance.class';
import { formatClassName } from '../utils/format-class-name';
import { SERVICE_METADATA_DEFAULTS } from '../constants/service-defaults.const';
import { BUILT_INS } from '../constants/builtins.const';
import { CannotInstantiateBuiltInError } from '../error/cannot-instantiate-builtin-error';
import { wrapDependencyAsResolvable } from '../utils/wrap-resolvable-dependency';
import { CannotInstantiateValueError } from '../error/cannot-instantiate-value.error';
export function Service(optionsOrDependencies, maybeDependencies) {
    return targetConstructor => {
        if (optionsOrDependencies == null || targetConstructor == null) {
            // todo: more info in these error messages!!!
            throw Error('The required configuration was not passed.');
        }
        /** A list of dependencies resolved from the arguments provided to the function. */
        let resolvedDependencies;
        if (Array.isArray(optionsOrDependencies)) {
            /**
             * If our first argument is an array, then the user has not specified any options,
             * and has instead filled the slot with a list of dependencies.
             */
            resolvedDependencies = optionsOrDependencies;
        }
        else if (Array.isArray(maybeDependencies)) {
            resolvedDependencies = maybeDependencies;
        }
        else if ('dependencies' in optionsOrDependencies) {
            /**
             * The first argument is of type `ServiceOptions<T>` with dependencies.
             * We can access its "dependencies" property and then map the unwrapped types.
             */
            resolvedDependencies = optionsOrDependencies.dependencies;
        }
        if (!resolvedDependencies) {
            /** At this point we have exhausted all options, so throw. */
            throw Error('The dependencies provided were not able to be resolved.');
        }
        const wrappedDependencies = resolvedDependencies.map(wrapDependencyAsResolvable);
        /**
         * A default list of options for this service.
         * These are used when the options are not explicitly provided to the function.
         * However, if options are passed, these are merged in as defaults.
         */
        let metadata = {
            id: targetConstructor,
            type: targetConstructor,
            ...SERVICE_METADATA_DEFAULTS,
            container: ContainerInstance.defaultContainer,
        };
        if (!Array.isArray(optionsOrDependencies)) {
            metadata = { ...metadata, ...optionsOrDependencies };
            /**
             * Remove the decorators from the options forcibly.
             * Otherwise, our type-wrapped dependencies passed to ContainerInstance.set are ignored.
             */
            delete metadata.dependencies;
        }
        const { id, container } = metadata;
        /**
         * If the target is already registered, `@Service` has been called twice on the same constructor.
         * Throw an error, as not doing so would raise ambiguity regarding the implementation.
         * This is most likely user error, as the function should __never__ be called twice.
         */
        if (container.has(id) && !metadata.multiple) {
            throw Error(`@Service() has been called twice upon ${formatClassName(targetConstructor)}, or you have used an ID twice.`);
        }
        /**
         * Check any available eager types immediately, so we can quickly raise an error
         * if they are invalid, instead of when the service is injected.
         */
        wrappedDependencies.forEach(({ typeWrapper }, index) => {
            const { eagerType } = typeWrapper;
            if (eagerType !== null) {
                const type = typeof typeWrapper;
                if (type !== 'function' && type !== 'object' && type !== 'string') {
                    throw new CannotInstantiateValueError(`The identifier provided at index ${index} for service ${formatClassName(targetConstructor)} is invalid.`);
                }
                else if (metadata.factory == null && BUILT_INS.includes(eagerType)) {
                    /**
                     * Ensure the service does not contain built-in types (Number, Symbol, Object, etc.)
                     * without also holding a factory to manually create an instance of the constructor.
                     */
                    throw new CannotInstantiateBuiltInError(eagerType?.name ?? eagerType);
                }
            }
        });
        /**
         * The `.set(Omit<ServiceOptions<unknown>, "dependencies">, TypeWrapper[])` overload is used here.
         * TypeScript downcasts the metadata to the type above.
         *
         * By default, this is set to `defaultContainer`.
         * Therefore, it will be bound to that.
         */
        container.set(metadata, wrappedDependencies);
    };
}
//# sourceMappingURL=service.decorator.js.map